import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronDown, ChevronUp, Route as RouteIcon, MapPin, CheckCircle2, DollarSign } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Driver-facing "My Timesheet" page.
 *
 * Fetches `/api/timesheets/me` which returns the driver's own worked-days
 * grouped by ISO week (Monday → Sunday). Each week is a collapsible card
 * that lists all 7 days with a visual mark for "worked" days plus the
 * routes / deliveries / kilometers for that day.
 *
 * The route-list totals come straight from the backend so the client
 * doesn't have to re-count anything — keeps the code simple and avoids
 * discrepancies between per-day and per-week totals.
 */
export default function DriverTimesheet() {
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(0);   // most-recent week expanded by default
  // Scope: "this_week" | "last_week" | "month". Everything below derives
  // from a single fetch of the last 6 ISO weeks — that covers any calendar
  // month even when it straddles week boundaries.
  const [scope, setScope] = useState("this_week");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/timesheets/me`, { params: { weeks: 6 } })
      .then((r) => { if (!cancelled) setWeeks(r.data.weeks || []); })
      .catch((e) => toast.error(`Could not load timesheet: ${formatApiError(e)}`))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const fmtDayShort = (iso) => {
    // "2026-07-06" → "Jul 6"
    const d = new Date(`${iso}T12:00:00Z`);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
  };

  // Filter the raw weeks/days down to whatever the operator picked. We
  // always compute using UTC-anchored ISO dates the backend already sends
  // so we don't drift across timezone boundaries.
  const scoped = useMemo(() => {
    if (!weeks.length) return { weeks: [], label: "" };
    if (scope === "this_week") {
      return { weeks: weeks.slice(0, 1), label: "this week" };
    }
    if (scope === "last_week") {
      return { weeks: weeks.slice(1, 2), label: "last week" };
    }
    // Month: keep only DAYS in the current calendar month, then rebuild
    // the per-week totals from those days so the summary and the week
    // cards stay perfectly consistent.
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth(); // 0-indexed
    const monthLabel = now.toLocaleDateString(undefined, { month: "long" }).toLowerCase();
    const filtered = weeks
      .map((w) => {
        const days = (w.days || []).filter((d) => {
          const dt = new Date(`${d.date}T12:00:00Z`);
          return dt.getUTCFullYear() === y && dt.getUTCMonth() === m;
        });
        if (!days.length) return null;
        const total_earnings = days.reduce((s, d) => s + (d.earnings || 0), 0);
        const worked_days = days.filter((d) => d.worked).length;
        const total_routes = days.reduce((s, d) => s + (d.routes || 0), 0);
        const total_stops_delivered = days.reduce((s, d) => s + (d.stops_delivered || 0), 0);
        const total_distance_km = days.reduce((s, d) => s + (d.distance_km || 0), 0);
        return {
          ...w,
          days,
          total_earnings: Number(total_earnings.toFixed(2)),
          worked_days,
          total_routes,
          total_stops_delivered,
          total_distance_km: Number(total_distance_km.toFixed(1)),
        };
      })
      .filter(Boolean);
    return { weeks: filtered, label: monthLabel };
  }, [weeks, scope]);

  const displayWeeks = scoped.weeks;

  return (
    <div className="max-w-md mx-auto p-4 pb-24" data-testid="driver-timesheet-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/driver" className="p-2 rounded-full hover:bg-secondary" data-testid="driver-timesheet-back">
          <ChevronLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-xl font-display font-semibold">My Timesheet</h1>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-muted-foreground">Show:</span>
        {[
          { key: "this_week", label: "This Week" },
          { key: "last_week", label: "Last Week" },
          { key: "month",     label: "Month" },
        ].map((opt) => (
          <Button
            key={opt.key}
            variant={scope === opt.key ? "default" : "outline"}
            size="sm"
            className="h-7 rounded-full px-3 text-xs"
            onClick={() => { setScope(opt.key); setOpenIdx(0); }}
            data-testid={`timesheet-range-${opt.key}`}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {loading && (
        <Card className="p-4 rounded-2xl text-sm text-muted-foreground" data-testid="timesheet-loading">
          Loading…
        </Card>
      )}

      {!loading && displayWeeks.length > 0 && (
        <Card
          className="p-4 rounded-2xl mb-4 border-2 border-[#0f766e]/40 bg-gradient-to-br from-[#e6fffb] to-[#c9f2ea]"
          data-testid="timesheet-grand-total"
        >
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-[#0f766e] text-white grid place-items-center shrink-0">
              <DollarSign className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#0f4741]">
                Earnings · {scoped.label}
              </div>
              <div className="text-2xl font-mono-num font-bold text-[#0f4741] leading-tight">
                {fmtMoney(displayWeeks.reduce((sum, w) => sum + (w.total_earnings || 0), 0))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {displayWeeks.reduce((s, w) => s + w.worked_days, 0)} worked days · {displayWeeks.reduce((s, w) => s + w.total_routes, 0)} routes
              </div>
            </div>
          </div>
        </Card>
      )}

      {!loading && displayWeeks.length === 0 && (
        <Card className="p-4 rounded-2xl text-sm text-muted-foreground" data-testid="timesheet-empty">
          No route activity in this period.
        </Card>
      )}

      {!loading && displayWeeks.map((wk, idx) => {
        const isOpen = openIdx === idx;
        const hasWork = wk.worked_days > 0;
        return (
          <Card
            key={wk.start}
            className={`mb-3 rounded-2xl border-2 ${hasWork ? "border-[#0f766e]/30 bg-[#f4faf9]" : "border-border bg-card"}`}
            data-testid={`timesheet-week-${wk.start}`}
          >
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? -1 : idx)}
              className="w-full p-4 flex items-center gap-3 text-left"
              data-testid={`timesheet-week-toggle-${wk.start}`}
            >
              <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${hasWork ? "bg-[#0f766e] text-white" : "bg-secondary text-muted-foreground"}`}>
                <span className="text-xs font-bold font-mono-num">{wk.worked_days}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">
                  {fmtDayShort(wk.start)} – {fmtDayShort(wk.end)}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2">
                  <span>{wk.worked_days}/{wk.days.length} days worked</span>
                  <span>·</span>
                  <span>{wk.total_routes} route{wk.total_routes === 1 ? "" : "s"}</span>
                  <span>·</span>
                  <span>{wk.total_stops_delivered} deliveries</span>
                  <span>·</span>
                  <span>{wk.total_distance_km} km</span>
                </div>
                {wk.total_earnings > 0 && (
                  <div className="text-xs font-mono-num font-semibold text-[#0f4741] mt-0.5" data-testid={`timesheet-week-earnings-${wk.start}`}>
                    You earned {fmtMoney(wk.total_earnings)}
                  </div>
                )}
              </div>
              {isOpen
                ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
            </button>

            {isOpen && (
              <ol className="px-4 pb-4 space-y-1.5" data-testid={`timesheet-week-days-${wk.start}`}>
                {wk.days.map((d) => (
                  <li
                    key={d.date}
                    className={`flex items-center gap-3 p-2.5 rounded-lg text-sm ${d.worked ? "bg-white dark:bg-secondary/50 border border-[#0f766e]/20" : "bg-transparent"}`}
                    data-testid={`timesheet-day-${d.date}`}
                  >
                    <div className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold shrink-0 ${d.worked ? "bg-[#0f766e] text-white" : "bg-muted text-muted-foreground/60"}`}>
                      {d.weekday[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs ${d.worked ? "font-medium" : "text-muted-foreground"}`}>
                        {d.weekday} · {fmtDayShort(d.date)}
                      </div>
                      {d.worked && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                          <span className="inline-flex items-center gap-1"><RouteIcon className="h-3 w-3" />{d.routes}</span>
                          <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{d.stops_delivered}</span>
                          <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{d.distance_km} km</span>
                          <span className="inline-flex items-center gap-1 font-mono-num font-semibold text-[#0f4741]"><DollarSign className="h-3 w-3" />{fmtMoney(d.earnings).replace("$", "")}</span>
                        </div>
                      )}
                    </div>
                    {d.worked && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0f766e]/10 text-[#0f4741]">Worked</span>}
                  </li>
                ))}
              </ol>
            )}
          </Card>
        );
      })}
    </div>
  );
}
